import ResizeObserver from 'resize-observer-polyfill';

const Easing = {
  // no easing, no acceleration
  linear: (t) => {
    return t;
  },
  // accelerating from zero velocity
  easeInQuad: (t) => {
    return t * t;
  },
  // decelerating to zero velocity
  easeOutQuad: (t) => {
    return t * (2 - t);
  },
  // acceleration until halfway, then deceleration
  easeInOutQuad: (t) => {
    return t < .5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
  },
  // accelerating from zero velocity
  easeInCubic: (t) => {
    return t * t * t;
  },
  // decelerating to zero velocity
  easeOutCubic: (t) => {
    return (--t) * t * t + 1;
  },
  // acceleration until halfway, then deceleration
  easeInOutCubic: (t) => {
    return t < .5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
  },
  // accelerating from zero velocity
  easeInQuart: (t) => {
    return t * t * t * t;
  },
  // decelerating to zero velocity
  easeOutQuart: (t) => {
    return 1 - (--t) * t * t * t;
  },
  // acceleration until halfway, then deceleration
  easeInOutQuart: (t) => {
    return t < .5 ? 8 * t * t * t * t : 1 - 8 * (--t) * t * t * t;
  },
  // accelerating from zero velocity
  easeInQuint: (t) => {
    return t * t * t * t * t;
  },
  // decelerating to zero velocity
  easeOutQuint: (t) => {
    return 1 + (--t) * t * t * t * t;
  },
  // acceleration until halfway, then deceleration
  easeInOutQuint: (t) => {
    return t < .5 ? 16 * t * t * t * t * t : 1 + 16 * (--t) * t * t * t * t;
  }
};

const defOpt = {
  maxScale: 2,
  minScale: 1,
  zoomScaleUnit: 0.2,
  // true to change relative position(according to cursor positon) when zooming.
  zoomTranslate: false,
  zoomSensitivity: 0.1,
  zoomEasing: 'easeOutQuart',
  zoomAnimate: true,
  zoomAnimateDuration: 200,
  dblclickZooming: true,

  draggable: false,

  onWheel: () => true,
  onZoom: () => {},
  onDrag: () => {},
  onResize: () => {}
};

export default class ZoomHelper {
  constructor(container, opt) {
    this.opt = Object.assign({}, defOpt, opt);
    this._container = container;
    this._scale = 1;
    this._translateX = 0;
    this._translateY = 0;

    this._containerRO = new ResizeObserver(this._onContainerResize.bind(this));
    this._containerRO.observe(this._container);

    this._onMouseWheelListener = this._onMouseWheel.bind(this);
    this._onMouseDownListener = this._onMouseDown.bind(this);
    this._onMouseUpListener = this._onMouseUp.bind(this);
    this._onMouseMoveListener = this._onMouseMove.bind(this);
    this._onDoubleClickListener = this._onDoubleClick.bind(this);

    // bind evnets
    container.addEventListener('wheel', this._onMouseWheelListener);
    container.addEventListener('mousedown', this._onMouseDownListener);
    container.addEventListener('mouseup', this._onMouseUpListener);
    container.addEventListener('mousemove', this._onMouseMoveListener);
    if (this.opt.dblclickZooming) {
      container.addEventListener('dblclick', this._onDoubleClickListener);
    }
  }

  set draggable(v) {
    this.opt.draggable = v || false;
  }

  get draggable() {
    return this.opt.draggable;
  }

  setScale(scale) {
    // scale = scale - this._scale;
    // if (scale === 0) {
    //   return;
    // }
    this.zoom(scale-this._scale);
  }

  zoom(scaleAmount, translateX = null, translateY = null, applySensitivity = true) {
    this.stopZooming();

    let startX = this._translateX;
    let startY = this._translateY;
    let startScale = this._scale;

    let {width, height} = this._getElementInnerSize(this._container);
    let centerX = width / 2;
    let centerY = height / 2;

    let isZoomIn = scaleAmount > 0 ? true : false;

    this._scale += scaleAmount;
    let maxScale = this.opt.maxScale;
    let minScale = this.opt.minScale;
    // if new scale is smaller than minScale, reset scale to minScale and reset position to center.
    if (this._scale <= minScale) {
      this._scale = minScale;
      this._translateX = 0;
      this._translateY = 0;
    // if new scale is greater than maxScale, set scale to maxScale
    } else if (maxScale && this._scale > maxScale) {
      this._scale = maxScale;
    } else if (this.opt.zoomTranslate) {
      if (translateX !== null && translateY !== null) {
        let distX = translateX - centerX;
        let distY = translateY - centerY;
        distX = isZoomIn ? distX * -1 : distX;
        distY = isZoomIn ? distY * -1 : distY;
        let distConst = applySensitivity ? this.opt.zoomSensitivity : 1;
        this._translateX = distX * distConst + this._translateX;
        this._translateY = distY * distConst + this._translateY;
      }
    }

    this._animateZoom(startX, startY, startScale);
  }

  drag(translateX, translateY) {
    this._translateX = translateX;
    this._translateY = translateY;
    if (typeof this.opt.onDrag === 'function') {
      let zoomInfo = this.getZoomInfo();
      this.opt.onDrag(zoomInfo);
    }
    return;
  }

  stopZooming() {
    clearInterval(this._animateZoomId);
  }

  reset() {
    let startX = this._translateX;
    let startY = this._translateY;
    let startScale = this._scale;

    this._doubleClickZoom = false;
    this._scale = 1;
    this._translateX = 0;
    this._translateY = 0;
    this._container.style.cursor = 'auto';

    this._animateZoom(startX, startY, startScale);
  }

  destroy() {
    if (this._container) {
      this._container.addEventListener('wheel', this._onMouseWheelListener);
      this._container.addEventListener('mousedown', this._onMouseDownListener);
      this._container.addEventListener('mouseup', this._onMouseUpListener);
      this._container.addEventListener('mousemove', this._onMouseMoveListener);
      this._container.addEventListener('dblclick', this._onDoubleClickListener);
    }
  }

  getZoomInfo() {
    return {
      translateX: this._translateX,
      translateY: this._translateY,
      scale: this._scale,
      container: this._container.getBoundingClientRect()
    };
  }

  _onContainerResize() {
    if (typeof this.opt.onResize === 'function') {
      this.opt.onResize(this.getZoomInfo());
    }
  }

  _onMouseWheel(e) {
    if (this.opt.onWheel(e) === false) {
      return;
    }
    let scaleAmount = e.deltaY > 0 ? -this.opt.zoomScaleUnit : this.opt.zoomScaleUnit;
    this.zoom(scaleAmount, e.clientX, e.clientY);
  }

  _animateZoom(startX, startY, startScale) {
    if (!this.opt.zoomAnimate) {
      let zoomInfo = this.getZoomInfo();
      this.opt.onZoom(zoomInfo, true);
      return;
    }
    let distX = this._translateX - startX;
    let distY = this._translateY - startY;
    let scaleDist = this._scale - startScale;

    let duration = this.opt.zoomAnimateDuration;
    let easing = this.opt.zoomEasing;
    let startTime = new Date().getTime();

    let easingZoomFrame = () => {
      let timestamp = new Date().getTime();
      let progressTime = timestamp - startTime;
      let percentage = (progressTime / duration);

      this._translateX = startX + this._getEasingValue(distX, percentage, easing);
      this._translateY = startY + this._getEasingValue(distY, percentage, easing);
      this._scale = startScale + this._getEasingValue(scaleDist, percentage, easing);
      if (this._scale < 1) {
        this._scale = 1;
        this._translateX = 0;
        this._translateY = 0;
      }

      // trigger onZoom event with zomming information, including container & target size、scale、translation.
      // It's necessary for minimap to update view box position and size when zomming action occur.
      if (typeof this.opt.onZoom === 'function') {
        let zoomInfo = this.getZoomInfo();
        this.opt.onZoom(zoomInfo);
      }

      if (progressTime >= duration) {
        this.stopZooming();
        let zoomInfo = this.getZoomInfo();
        this.opt.onZoom(zoomInfo, true);
      }
    };

    this._animateZoomId = setInterval(easingZoomFrame, 20);
  }

  _getEasingValue(total, percentage, easing) {
    percentage = (percentage > 1) ? 1 : percentage;
    let result = total * Easing[easing](percentage);
    return result;
  }

  _onDoubleClick(e) {
    e.stopPropagation();
    this._doubleClickZoom = !this._doubleClickZoom;
    // toggle scale from 1 to 2
    if (this._doubleClickZoom) {
      let scaleAmount = this._scale > 2 ? 2-this._scale : 1;
      this.zoom(scaleAmount, e.clientX, e.clientY, false);
    } else {
      this.reset();
    }
  }

  _onMouseDown(e) {
    // prevent double click to select text automatically
    e.preventDefault();
    if (e.button !== 0) {
      return;
    }
    this._isDragging = true;
  }

  _onMouseUp(e) {
    if (e.button !== 0) {
      return;
    }
    this._isDragging = false;
  }

  _onMouseMove(e) {
    if (!this._isDragging) {
      return;
    }
    this.stopZooming();
    if (!this.opt.draggable) {
      return;
    }
    let translateX = this._translateX + e.movementX;
    let translateY = this._translateY + e.movementY;
    this.drag(translateX, translateY);
  }

  _getElementInnerSize(el) {
    let height = el.clientHeight;
    let width = el.clientWidth;
    let paddingLeft = window.getComputedStyle(el, null).getPropertyValue('padding-left');
    let paddingRight = window.getComputedStyle(el, null).getPropertyValue('padding-right');
    let paddingTop = window.getComputedStyle(el, null).getPropertyValue('padding-top');
    let paddingBottom = window.getComputedStyle(el, null).getPropertyValue('padding-bottom');

    width = width - parseFloat(paddingLeft) - parseFloat(paddingRight);
    height = height - parseFloat(paddingTop) - parseFloat(paddingBottom);

    return {width, height};
  }
}
